/**
 * Finance View Contracts
 *
 * Canonical DSL column definitions, field definitions, and list/form templates
 * for all finance entities (ledger entries, ledger lines).
 *
 * These are NOT fallback "guess" templates — they are the authoritative source of
 * truth for how finance data is displayed.  Import from here when you need finance
 * column sets or form field lists.  The fallback-view-templates.ts file now re-exports
 * from here so existing template-resolution code continues to work.
 */

import type { ViewTemplateDSL } from '@/lib/use-view-template';

// ─────────────────────────────────────────────────────────────────────────────
// List Column Sets
// ─────────────────────────────────────────────────────────────────────────────

export const FINANCE_BILL_ENTRY_LIST_COLUMNS: NonNullable<ViewTemplateDSL['columns']> = [
  { field: 'entryNumber', label: 'Doc No', width: 150, pinned: 'left' },
  { field: 'entryType', label: 'Type', width: 100, format: 'status' },
  { field: 'status', label: 'Status', width: 110, format: 'status' },
  { field: 'customer_name', label: 'Customer', width: 180 },
  { field: 'vendor_name', label: 'Vendor', width: 180 },
  { field: 'job_number', label: 'Job No', width: 130 },
  { field: 'shipment_nos', label: 'Shipment No', width: 160 },
  { field: 'currency', label: 'Ccy', width: 80 },
  { field: 'totalAmount', label: 'Amount', width: 130, format: 'currency' },
  { field: 'paidAmount', label: 'Paid', width: 130, format: 'currency' },
  { field: 'balance_amount', label: 'Balance', width: 130, format: 'currency' },
  { field: 'dueDate', label: 'Due Date', width: 120, format: 'date', editable: true },
  { field: 'postedAt', label: 'Posted', width: 150, format: 'datetime' },
  { field: 'createdAt', label: 'Created', width: 150, format: 'datetime', pinned: 'right' },
  { field: 'updatedAt', label: 'Modified', width: 150, format: 'datetime', pinned: 'right' },
];

export const FINANCE_JOURNAL_ENTRY_LIST_COLUMNS: NonNullable<ViewTemplateDSL['columns']> = [
  { field: 'entryNumber', label: 'Journal No', width: 150, pinned: 'left' },
  { field: 'status', label: 'Status', width: 110, format: 'status' },
  { field: 'entryDate', label: 'Journal Date', width: 130, format: 'date' },
  { field: 'narration', label: 'Narration', width: 260 },
  { field: 'currency', label: 'Ccy', width: 80 },
  { field: 'totalAmount', label: 'Gross', width: 130, format: 'currency' },
  { field: 'balance_amount', label: 'Balance Gap', width: 130, format: 'currency' },
  { field: 'postedAt', label: 'Posted', width: 150, format: 'datetime' },
  { field: 'createdAt', label: 'Created', width: 150, format: 'datetime' },
  { field: 'updatedAt', label: 'Modified', width: 150, format: 'datetime', pinned: 'right' },
];

export const FINANCE_BILL_LINE_LIST_COLUMNS: NonNullable<ViewTemplateDSL['columns']> = [
  { field: 'lineNo', label: 'Line', width: 70, format: 'number' },
  { field: 'description', label: 'Description', width: 260, editable: true },
  { field: 'quantity', label: 'Qty', width: 90, format: 'number', editable: true },
  { field: 'unitPrice', label: 'Unit Price', width: 130, format: 'currency', editable: true },
  { field: 'amount', label: 'Amount', width: 130, format: 'currency' },
  { field: 'taxRate', label: 'Tax %', width: 90, format: 'number', editable: true },
  { field: 'taxAmount', label: 'Tax Amt', width: 130, format: 'currency' },
  { field: 'debit', label: 'Debit', width: 130, format: 'currency' },
  { field: 'credit', label: 'Credit', width: 130, format: 'currency' },
];

export const FINANCE_JOURNAL_LINE_LIST_COLUMNS: NonNullable<ViewTemplateDSL['columns']> = [
  { field: 'lineNo', label: 'Line', width: 70, format: 'number' },
  { field: 'accountId', label: 'GL Account', width: 180 },
  { field: 'description', label: 'Description', width: 280, editable: true },
  { field: 'debit', label: 'Debit', width: 140, format: 'currency', editable: true },
  { field: 'credit', label: 'Credit', width: 140, format: 'currency', editable: true },
  { field: 'createdAt', label: 'Created', width: 150, format: 'datetime' },
];

// ─────────────────────────────────────────────────────────────────────────────
// Form Field Definitions
// ─────────────────────────────────────────────────────────────────────────────

export const FINANCE_BILL_ENTRY_FORM_FIELDS: NonNullable<ViewTemplateDSL['fields']> = [
  { field: 'entryNumber', label: 'Document No', required: true, group: 'Header' },
  {
    field: 'entryType',
    label: 'Document Type',
    required: true,
    type: 'select',
    group: 'Header',
    options: [
      { label: 'AR Invoice', value: 'AR' },
      { label: 'AP Bill', value: 'AP' },
    ],
  },
  { field: 'jobId', label: 'Job', type: 'lookup', group: 'Header' },
  { field: 'customerId', label: 'Customer Party', type: 'lookup', group: 'Parties' },
  { field: 'vendorId', label: 'Vendor Party', type: 'lookup', group: 'Parties' },
  { field: 'currency', label: 'Currency', required: true, group: 'Amounts' },
  { field: 'exchangeRate', label: 'Exchange Rate', type: 'number', required: true, group: 'Amounts' },
  { field: 'entryDate', label: 'Entry Date', type: 'date', group: 'Timing' },
  { field: 'dueDate', label: 'Due Date', type: 'date', group: 'Timing' },
  { field: 'narration', label: 'Narration', type: 'textarea', span: 2, group: 'Notes' },
];

export const FINANCE_JOURNAL_ENTRY_FORM_FIELDS: NonNullable<ViewTemplateDSL['fields']> = [
  { field: 'entryNumber', label: 'Journal No', required: true, group: 'Header' },
  { field: 'currency', label: 'Currency', required: true, group: 'Amounts' },
  { field: 'exchangeRate', label: 'Exchange Rate', type: 'number', required: true, group: 'Amounts' },
  { field: 'entryDate', label: 'Journal Date', type: 'date', required: true, group: 'Timing' },
  { field: 'narration', label: 'Narration', type: 'textarea', span: 2, group: 'Notes' },
];

export const FINANCE_BILL_LINE_FORM_FIELDS: NonNullable<ViewTemplateDSL['fields']> = [
  { field: 'lineNo', label: 'Line No', type: 'number', group: 'Line' },
  { field: 'accountId', label: 'GL Account', type: 'lookup', group: 'Line' },
  { field: 'chargeCodeId', label: 'Charge Code', type: 'lookup', group: 'Line' },
  { field: 'description', label: 'Description', required: true, span: 2, group: 'Line' },
  { field: 'quantity', label: 'Quantity', type: 'number', required: true, group: 'Amounts' },
  { field: 'unitPrice', label: 'Unit Price', type: 'number', required: true, group: 'Amounts' },
  { field: 'taxCodeId', label: 'Tax Code', type: 'lookup', group: 'Tax' },
  { field: 'taxRate', label: 'Tax Rate %', type: 'number', group: 'Tax' },
];

export const FINANCE_JOURNAL_LINE_FORM_FIELDS: NonNullable<ViewTemplateDSL['fields']> = [
  { field: 'lineNo', label: 'Line No', type: 'number', group: 'Line' },
  { field: 'accountId', label: 'GL Account', type: 'lookup', required: true, group: 'Line' },
  { field: 'description', label: 'Description', required: true, span: 2, group: 'Line' },
  { field: 'debit', label: 'Debit', type: 'number', group: 'Amounts' },
  { field: 'credit', label: 'Credit', type: 'number', group: 'Amounts' },
];

// ─────────────────────────────────────────────────────────────────────────────
// Full Template Objects (list + form combined)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Canonical list template for the Billing Workspace (slug: finance-bill-entry-list).
 * Covers AR Invoice and AP Bill document types.
 */
export const FINANCE_BILL_ENTRY_LIST_TEMPLATE: ViewTemplateDSL = {
  columns: FINANCE_BILL_ENTRY_LIST_COLUMNS,
  density: 'compact',
  layout: { desktopColumns: 6, mobileColumns: 2, pageMode: 'major-list', surface: 'desktop-first' },
  page: {
    title: 'Billing Workspace',
    subtitle: 'Create, review, and post AR/AP bill headers without losing line-level control',
    searchPlaceholder: 'Search by doc no / customer / vendor / job / shipment',
  },
  list: {
    kind: 'major-grid',
    lineMode: '1|3',
    sorting: 'server',
    paging: 'server',
    search: { enabled: true, placeholder: 'Search by doc no / customer / vendor / job / shipment' },
    topFilters: [
      { key: 'entryType', label: 'Doc Type', type: 'multi-select' },
      { key: 'status', label: 'Status', type: 'multi-select' },
    ],
    columnSets: [
      {
        id: 'billing',
        label: 'Billing',
        description: 'Document, party, and balance control',
        visibleColumns: ['entryNumber', 'entryType', 'status', 'customer_name', 'vendor_name', 'job_number', 'currency', 'totalAmount', 'paidAmount', 'balance_amount', 'dueDate'],
      },
      {
        id: 'audit',
        label: 'Audit',
        description: 'Timing and traceability fields',
        visibleColumns: ['entryNumber', 'entryType', 'status', 'job_number', 'shipment_nos', 'currency', 'totalAmount', 'dueDate', 'postedAt', 'createdAt', 'updatedAt'],
      },
    ],
    savedViews: true,
    rowActions: ['view', 'edit', 'copy'],
  },
  panels: {
    main: { type: 'workspace-grid', frozenColumns: 1, allowInlineEdit: true },
    assist: {
      enabled: true,
      side: 'right',
      mode: 'sliding-resizable',
      resizable: true,
      collapsible: true,
      defaultOpen: true,
      width: 'lg',
      allowedContent: ['grid', 'ai', 'exceptions'],
    },
  },
  detailDock: {
    enabled: true,
    side: 'right',
    mode: 'dock-sheet',
    width: 'xl',
    openFrom: ['row-click', 'action-button'],
    defaultViewMode: 'edit',
    allowEdit: true,
    form: { desktopColumns: 6, mobileColumns: 2 },
    subLists: [
      { id: 'lines', label: 'Bill Lines', slug: 'ledger-lines', queryParam: 'entryId', sourceField: 'id', editable: 'row', actions: ['add', 'edit', 'delete'] },
      { id: 'payments', label: 'Payments', slug: 'payments', queryParam: 'entryId', sourceField: 'id', editable: 'none', actions: ['open'] },
    ],
  },
};

/**
 * Canonical list template for the Journal Workspace (slug: finance-journal-entry-list).
 */
export const FINANCE_JOURNAL_ENTRY_LIST_TEMPLATE: ViewTemplateDSL = {
  columns: FINANCE_JOURNAL_ENTRY_LIST_COLUMNS,
  density: 'compact',
  layout: { desktopColumns: 6, mobileColumns: 2, pageMode: 'major-list', surface: 'desktop-first' },
  page: {
    title: 'Journal Workspace',
    subtitle: 'Create, review, and post manual journals with explicit debit and credit control',
    searchPlaceholder: 'Search by journal no / narration / period',
  },
  list: {
    kind: 'major-grid',
    lineMode: '1|3',
    sorting: 'server',
    paging: 'server',
    search: { enabled: true, placeholder: 'Search by journal no / narration / period' },
    topFilters: [
      { key: 'status', label: 'Status', type: 'multi-select' },
    ],
    columnSets: [
      {
        id: 'journal',
        label: 'Journal',
        description: 'Posting and balance control',
        visibleColumns: ['entryNumber', 'status', 'entryDate', 'narration', 'currency', 'totalAmount', 'balance_amount', 'postedAt'],
      },
      {
        id: 'audit',
        label: 'Audit',
        description: 'Traceability and timing',
        visibleColumns: ['entryNumber', 'status', 'entryDate', 'currency', 'totalAmount', 'postedAt', 'createdAt', 'updatedAt'],
      },
    ],
    savedViews: true,
    rowActions: ['view', 'edit', 'copy'],
  },
  panels: {
    main: { type: 'workspace-grid', frozenColumns: 1, allowInlineEdit: false },
    assist: {
      enabled: true,
      side: 'right',
      mode: 'sliding-resizable',
      resizable: true,
      collapsible: true,
      defaultOpen: true,
      width: 'lg',
      allowedContent: ['grid', 'ai', 'exceptions'],
    },
  },
  detailDock: {
    enabled: true,
    side: 'right',
    mode: 'dock-sheet',
    width: 'xl',
    openFrom: ['row-click', 'action-button'],
    defaultViewMode: 'edit',
    allowEdit: true,
    form: { desktopColumns: 6, mobileColumns: 2 },
    subLists: [
      { id: 'lines', label: 'Journal Lines', slug: 'ledger-lines', queryParam: 'entryId', sourceField: 'id', editable: 'row', actions: ['add', 'edit', 'delete'] },
    ],
  },
};

/** Compact form template for AR/AP bill header (slug: finance-bill-entry-form). */
export const FINANCE_BILL_ENTRY_FORM_TEMPLATE: ViewTemplateDSL = {
  layout: { desktopColumns: 6, mobileColumns: 2, pageMode: 'record', surface: 'desktop-first' },
  fields: FINANCE_BILL_ENTRY_FORM_FIELDS,
};

/** Compact form template for journal entry header (slug: finance-journal-entry-form). */
export const FINANCE_JOURNAL_ENTRY_FORM_TEMPLATE: ViewTemplateDSL = {
  layout: { desktopColumns: 6, mobileColumns: 2, pageMode: 'record', surface: 'desktop-first' },
  fields: FINANCE_JOURNAL_ENTRY_FORM_FIELDS,
};

/** Compact form template for AR/AP bill line (slug: finance-bill-line-form). */
export const FINANCE_BILL_LINE_FORM_TEMPLATE: ViewTemplateDSL = {
  layout: { desktopColumns: 6, mobileColumns: 2, pageMode: 'record', surface: 'desktop-first' },
  fields: FINANCE_BILL_LINE_FORM_FIELDS,
};

/** Compact form template for journal line (slug: finance-journal-line-form). */
export const FINANCE_JOURNAL_LINE_FORM_TEMPLATE: ViewTemplateDSL = {
  layout: { desktopColumns: 6, mobileColumns: 2, pageMode: 'record', surface: 'desktop-first' },
  fields: FINANCE_JOURNAL_LINE_FORM_FIELDS,
};

// ─────────────────────────────────────────────────────────────────────────────
// Classic-mode Templates (designMode = 'classic')
// Flat, compact, no assist panel, no detail dock, all columns visible.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Classic list for the Billing Workspace (AR Invoice / AP Bill).
 * All columns visible, 1-line rows, no assist panel, no detail dock.
 */
export const FINANCE_BILL_ENTRY_LIST_CLASSIC_TEMPLATE: ViewTemplateDSL = {
  columns: [
    { field: 'entryNumber',    label: 'Doc No',    width: 150, pinned: 'left' as const },
    { field: 'entryType',      label: 'Type',      width: 100, format: 'status' as const },
    { field: 'status',         label: 'Status',    width: 110, format: 'status' as const },
    { field: 'customer_name',  label: 'Customer',  width: 180 },
    { field: 'vendor_name',    label: 'Vendor',    width: 180 },
    { field: 'job_number',     label: 'Job No',    width: 130 },
    { field: 'shipment_nos',   label: 'Shipment',  width: 160 },
    { field: 'currency',       label: 'Ccy',       width: 80 },
    { field: 'totalAmount',    label: 'Amount',    width: 130, format: 'currency' as const },
    { field: 'paidAmount',     label: 'Paid',      width: 130, format: 'currency' as const },
    { field: 'balance_amount', label: 'Balance',   width: 130, format: 'currency' as const },
    { field: 'dueDate',        label: 'Due Date',  width: 120, format: 'date' as const },
    { field: 'postedAt',       label: 'Posted',    width: 130, format: 'datetime' as const },
    { field: 'createdAt',      label: 'Created',   width: 130, format: 'datetime' as const, pinned: 'right' as const },
  ],
  density: 'compact',
  layout: { desktopColumns: 6, mobileColumns: 2, pageMode: 'major-list', surface: 'compact-first' },
  list: { kind: 'major-grid', lineMode: '1-line', sorting: 'server', paging: 'server' },
};

/**
 * Classic list for the Journal Workspace (manual journals).
 * All columns visible, 1-line rows, no assist panel, no detail dock.
 */
export const FINANCE_JOURNAL_ENTRY_LIST_CLASSIC_TEMPLATE: ViewTemplateDSL = {
  columns: [
    { field: 'entryNumber',    label: 'Journal No',   width: 150, pinned: 'left' as const },
    { field: 'status',         label: 'Status',       width: 110, format: 'status' as const },
    { field: 'entryDate',      label: 'Journal Date', width: 130, format: 'date' as const },
    { field: 'narration',      label: 'Narration',    width: 280 },
    { field: 'currency',       label: 'Ccy',          width: 80 },
    { field: 'totalAmount',    label: 'Gross',        width: 130, format: 'currency' as const },
    { field: 'balance_amount', label: 'Balance Gap',  width: 130, format: 'currency' as const },
    { field: 'postedAt',       label: 'Posted',       width: 130, format: 'datetime' as const },
    { field: 'createdAt',      label: 'Created',      width: 130, format: 'datetime' as const, pinned: 'right' as const },
  ],
  density: 'compact',
  layout: { desktopColumns: 6, mobileColumns: 2, pageMode: 'major-list', surface: 'compact-first' },
  list: { kind: 'major-grid', lineMode: '1-line', sorting: 'server', paging: 'server' },
};

/**
 * Classic detail for AR Invoice / AP Bill — header (2-col) + bill lines sub-grid + settlement.
 * Mirrors legacy ArInvoiceEdit.aspx / ApPayableEdit.aspx layout.
 * subLists in detailDock are rendered inline below the header in Classic full-page mode.
 */
export const FINANCE_BILL_ENTRY_DETAIL_CLASSIC_TEMPLATE: ViewTemplateDSL = {
  sections: [
    {
      title: 'Document Header',
      columns: 2 as const,
      fields: [
        { field: 'entryNumber', label: 'Doc No' },
        { field: 'entryType',   label: 'Doc Type',    format: 'status' },
        { field: 'status',      label: 'Status',      format: 'status' },
        { field: 'entryDate',   label: 'Doc Date',    format: 'date' },
        { field: 'dueDate',     label: 'Due Date',    format: 'date' },
        { field: 'currency',    label: 'Currency' },
        { field: 'exchangeRate',label: 'Exchange Rate' },
        { field: 'narration',   label: 'Narration',   span: 2 },
      ],
    },
    {
      title: 'Parties',
      columns: 2 as const,
      fields: [
        { field: 'customer_name', label: 'Debtor / Customer' },
        { field: 'vendor_name',   label: 'Creditor / Vendor' },
        { field: 'job_number',    label: 'Job No' },
        { field: 'shipment_nos',  label: 'Shipment No' },
      ],
    },
    {
      title: 'Totals',
      columns: 2 as const,
      fields: [
        { field: 'totalAmount',    label: 'Invoice Amount', format: 'currency' },
        { field: 'taxAmount',      label: 'GST / Tax',      format: 'currency' },
        { field: 'paidAmount',     label: 'Paid Amount',    format: 'currency' },
        { field: 'balance_amount', label: 'Balance Due',    format: 'currency' },
        { field: 'postedAt',       label: 'Posted At',      format: 'datetime' },
      ],
    },
  ],
  density: 'compact',
  layout: { desktopColumns: 2, mobileColumns: 2, pageMode: 'record', surface: 'compact-first' },
  detailDock: {
    enabled: true,
    side: 'right',
    mode: 'dock-sheet',
    width: 'xl',
    openFrom: ['row-click'],
    defaultViewMode: 'edit',
    allowEdit: true,
    subLists: [
      {
        id: 'bill-lines',
        label: 'Invoice Lines',
        slug: 'ledger-lines',
        queryParam: 'entryId',
        sourceField: 'id',
        editable: 'row',
        actions: ['add', 'edit', 'delete'],
        columns: [
          { field: 'lineNo',      label: 'Line', width: 55, format: 'number' as const },
          { field: 'chargeCode',  label: 'Code', width: 110, editable: true },
          { field: 'description', label: 'Description', width: 240, editable: true },
          { field: 'quantity',    label: 'Qty',  width: 70,  format: 'number' as const, editable: true },
          { field: 'unitPrice',   label: 'Price', width: 110, format: 'currency' as const, editable: true },
          { field: 'gstType',     label: 'GST',  width: 65,  editable: true },
          { field: 'taxRate',     label: 'Tax%', width: 65,  format: 'number' as const, editable: true },
          { field: 'taxAmount',   label: 'Tax Amt', width: 100, format: 'currency' as const },
          { field: 'amount',      label: 'Doc Amt', width: 110, format: 'currency' as const },
          { field: 'localAmount', label: 'Loc Amt', width: 110, format: 'currency' as const },
          { field: 'accountCode', label: 'GL Code', width: 100, editable: true },
        ],
      },
      {
        id: 'settlements',
        label: 'Receipts / Payments Applied',
        slug: 'payments',
        queryParam: 'entryId',
        sourceField: 'id',
        editable: 'none',
        actions: ['open'],
        columns: [
          { field: 'docNumber',     label: 'Doc No',    width: 140, pinned: 'left' as const },
          { field: 'docType',       label: 'Type',      width: 80,  format: 'status' as const },
          { field: 'docDate',       label: 'Date',      width: 110, format: 'date' as const },
          { field: 'currency',      label: 'Ccy',       width: 70 },
          { field: 'appliedAmount', label: 'Applied',   width: 120, format: 'currency' as const },
          { field: 'chequeNo',      label: 'Cheque No', width: 120 },
        ],
      },
    ],
  },
};

/**
 * Classic detail for Journal Entry — header (2-col) + journal lines sub-grid (row-editable).
 * Mirrors legacy GlEntryEdit.aspx layout.
 */
export const FINANCE_JOURNAL_ENTRY_DETAIL_CLASSIC_TEMPLATE: ViewTemplateDSL = {
  sections: [
    {
      title: 'Journal Header',
      columns: 2 as const,
      fields: [
        { field: 'entryNumber', label: 'Journal No' },
        { field: 'status',      label: 'Status',       format: 'status' },
        { field: 'entryDate',   label: 'Journal Date', format: 'date' },
        { field: 'currency',    label: 'Currency' },
        { field: 'exchangeRate',label: 'Exchange Rate' },
        { field: 'totalAmount', label: 'Gross Amount', format: 'currency' },
        { field: 'narration',   label: 'Narration',    span: 2 },
      ],
    },
  ],
  density: 'compact',
  layout: { desktopColumns: 2, mobileColumns: 2, pageMode: 'record', surface: 'compact-first' },
  detailDock: {
    enabled: true,
    side: 'right',
    mode: 'dock-sheet',
    width: 'xl',
    openFrom: ['row-click'],
    defaultViewMode: 'edit',
    allowEdit: true,
    subLists: [
      {
        id: 'journal-lines',
        label: 'Journal Lines',
        slug: 'ledger-lines',
        queryParam: 'entryId',
        sourceField: 'id',
        editable: 'row',
        actions: ['add', 'edit', 'delete'],
        columns: [
          { field: 'lineNo',      label: 'Line',        width: 55,  format: 'number' as const },
          { field: 'accountCode', label: 'GL Account',  width: 160, editable: true },
          { field: 'description', label: 'Description', width: 280, editable: true },
          { field: 'debit',       label: 'Debit',       width: 140, format: 'currency' as const, editable: true },
          { field: 'credit',      label: 'Credit',      width: 140, format: 'currency' as const, editable: true },
        ],
      },
    ],
  },
};

// ─── AR Receipt & AP Payment Classic Templates ───────────────────────────────
// AR Receipt = customer pays us (ArReceipt.aspx / ArReceiptEdit.aspx)
// AP Payment = we pay vendor   (ApPayment.aspx / ApPaymentEdit.aspx)

/**
 * Classic list for AR Receipts (customer payments received).
 */
export const FINANCE_AR_RECEIPT_LIST_CLASSIC_TEMPLATE: ViewTemplateDSL = {
  columns: [
    { field: 'entryNumber',   label: 'Receipt No',  width: 150, pinned: 'left' as const },
    { field: 'status',        label: 'Status',      width: 100, format: 'status' as const },
    { field: 'entryDate',     label: 'Date',        width: 110, format: 'date' as const },
    { field: 'customer_name', label: 'Debtor',      width: 180 },
    { field: 'chequeNo',      label: 'Cheque No',   width: 130 },
    { field: 'bankAccount',   label: 'Bank Acct',   width: 120 },
    { field: 'currency',      label: 'Ccy',         width: 70 },
    { field: 'totalAmount',   label: 'Amount',      width: 130, format: 'currency' as const },
    { field: 'paidAmount',    label: 'Applied',     width: 130, format: 'currency' as const },
    { field: 'balance_amount',label: 'Unapplied',   width: 130, format: 'currency' as const },
    { field: 'postedAt',      label: 'Posted',      width: 130, format: 'datetime' as const },
    { field: 'createdAt',     label: 'Created',     width: 130, format: 'datetime' as const, pinned: 'right' as const },
  ],
  density: 'compact',
  layout: { desktopColumns: 6, mobileColumns: 2, pageMode: 'major-list', surface: 'compact-first' },
  list: { kind: 'major-grid', lineMode: '1-line', sorting: 'server', paging: 'server' },
};

/**
 * Classic detail for AR Receipt — header (2-col) + invoices cleared sub-grid.
 * Mirrors legacy ArReceiptEdit.aspx layout.
 */
export const FINANCE_AR_RECEIPT_DETAIL_CLASSIC_TEMPLATE: ViewTemplateDSL = {
  sections: [
    {
      title: 'Receipt Header',
      columns: 2 as const,
      fields: [
        { field: 'entryNumber',   label: 'Receipt No' },
        { field: 'entryDate',     label: 'Date',         format: 'date' },
        { field: 'customer_name', label: 'Debtor' },
        { field: 'status',        label: 'Status',       format: 'status' },
        { field: 'currency',      label: 'Currency' },
        { field: 'exchangeRate',  label: 'Exchange Rate' },
        { field: 'totalAmount',   label: 'Receipt Amount', format: 'currency' },
        { field: 'localAmount',   label: 'Loc Amount',     format: 'currency' },
        { field: 'chequeNo',      label: 'Cheque No' },
        { field: 'bankAccount',   label: 'Bank Account' },
        { field: 'narration',     label: 'Narration',    span: 2 },
      ],
    },
    {
      title: 'Applied Total',
      columns: 2 as const,
      fields: [
        { field: 'paidAmount',    label: 'Applied Amount', format: 'currency' },
        { field: 'balance_amount',label: 'Unapplied',      format: 'currency' },
        { field: 'postedAt',      label: 'Posted At',      format: 'datetime' },
      ],
    },
  ],
  density: 'compact',
  layout: { desktopColumns: 2, mobileColumns: 2, pageMode: 'record', surface: 'compact-first' },
  detailDock: {
    enabled: true,
    side: 'right',
    mode: 'dock-sheet',
    width: 'xl',
    openFrom: ['row-click'],
    defaultViewMode: 'edit',
    allowEdit: true,
    subLists: [
      {
        id: 'invoices-cleared',
        label: 'Invoices Cleared',
        slug: 'ledger-entries',
        queryParam: 'receiptId',
        sourceField: 'id',
        editable: 'row',
        actions: ['add', 'delete'],
        columns: [
          { field: 'invoiceNo',     label: 'Invoice No',   width: 150, pinned: 'left' as const },
          { field: 'invoiceDate',   label: 'Inv Date',     width: 110, format: 'date' as const },
          { field: 'currency',      label: 'Ccy',          width: 70 },
          { field: 'invoiceAmount', label: 'Invoice Amt',  width: 130, format: 'currency' as const },
          { field: 'appliedAmount', label: 'Applied Amt',  width: 130, format: 'currency' as const, editable: true },
          { field: 'balance',       label: 'Balance',      width: 130, format: 'currency' as const },
        ],
      },
    ],
  },
};

/**
 * Classic list for AP Payments (payments made to vendors).
 */
export const FINANCE_AP_PAYMENT_LIST_CLASSIC_TEMPLATE: ViewTemplateDSL = {
  columns: [
    { field: 'entryNumber',  label: 'Payment No', width: 150, pinned: 'left' as const },
    { field: 'status',       label: 'Status',     width: 100, format: 'status' as const },
    { field: 'entryDate',    label: 'Date',       width: 110, format: 'date' as const },
    { field: 'vendor_name',  label: 'Creditor',   width: 180 },
    { field: 'chequeNo',     label: 'Cheque No',  width: 130 },
    { field: 'bankAccount',  label: 'Bank Acct',  width: 120 },
    { field: 'currency',     label: 'Ccy',        width: 70 },
    { field: 'totalAmount',  label: 'Amount',     width: 130, format: 'currency' as const },
    { field: 'paidAmount',   label: 'Applied',    width: 130, format: 'currency' as const },
    { field: 'balance_amount',label: 'Unapplied', width: 130, format: 'currency' as const },
    { field: 'postedAt',     label: 'Posted',     width: 130, format: 'datetime' as const },
    { field: 'createdAt',    label: 'Created',    width: 130, format: 'datetime' as const, pinned: 'right' as const },
  ],
  density: 'compact',
  layout: { desktopColumns: 6, mobileColumns: 2, pageMode: 'major-list', surface: 'compact-first' },
  list: { kind: 'major-grid', lineMode: '1-line', sorting: 'server', paging: 'server' },
};

/**
 * Classic detail for AP Payment — header (2-col) + payables settled sub-grid.
 * Mirrors legacy ApPaymentEdit.aspx layout.
 */
export const FINANCE_AP_PAYMENT_DETAIL_CLASSIC_TEMPLATE: ViewTemplateDSL = {
  sections: [
    {
      title: 'Payment Header',
      columns: 2 as const,
      fields: [
        { field: 'entryNumber',  label: 'Payment No' },
        { field: 'entryDate',    label: 'Date',          format: 'date' },
        { field: 'vendor_name',  label: 'Creditor' },
        { field: 'status',       label: 'Status',        format: 'status' },
        { field: 'currency',     label: 'Currency' },
        { field: 'exchangeRate', label: 'Exchange Rate' },
        { field: 'totalAmount',  label: 'Payment Amount',format: 'currency' },
        { field: 'localAmount',  label: 'Loc Amount',    format: 'currency' },
        { field: 'chequeNo',     label: 'Cheque No' },
        { field: 'bankAccount',  label: 'Bank Account' },
        { field: 'narration',    label: 'Narration',     span: 2 },
      ],
    },
    {
      title: 'Applied Total',
      columns: 2 as const,
      fields: [
        { field: 'paidAmount',    label: 'Applied Amount', format: 'currency' },
        { field: 'balance_amount',label: 'Unapplied',      format: 'currency' },
        { field: 'postedAt',      label: 'Posted At',      format: 'datetime' },
      ],
    },
  ],
  density: 'compact',
  layout: { desktopColumns: 2, mobileColumns: 2, pageMode: 'record', surface: 'compact-first' },
  detailDock: {
    enabled: true,
    side: 'right',
    mode: 'dock-sheet',
    width: 'xl',
    openFrom: ['row-click'],
    defaultViewMode: 'edit',
    allowEdit: true,
    subLists: [
      {
        id: 'payables-settled',
        label: 'Payables Settled',
        slug: 'ledger-entries',
        queryParam: 'paymentId',
        sourceField: 'id',
        editable: 'row',
        actions: ['add', 'delete'],
        columns: [
          { field: 'invoiceNo',     label: 'Payable No',   width: 150, pinned: 'left' as const },
          { field: 'invoiceDate',   label: 'Bill Date',    width: 110, format: 'date' as const },
          { field: 'currency',      label: 'Ccy',          width: 70 },
          { field: 'invoiceAmount', label: 'Payable Amt',  width: 130, format: 'currency' as const },
          { field: 'appliedAmount', label: 'Applied Amt',  width: 130, format: 'currency' as const, editable: true },
          { field: 'balance',       label: 'Balance',      width: 130, format: 'currency' as const },
        ],
      },
    ],
  },
};
